import { motion } from 'framer-motion';
import React, { useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { IoCloseCircleOutline } from "react-icons/io5";
import { Link } from 'react-router-dom';
import usePreventBodyScroll from '../hooks/usePreventBodyScroll';
import SalesforceForm from './SalesforceForm';
import SimpleButton from './SimpleButton';
import TripleLine from './TripleLine';
import Complete from './animations/Complete';

const modalVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: {
        duration: 0.1,
        scale: 1,
        opacity: 1,
        transition: {
            duration: .2,
            type: "linear",
        }
    },
};

interface ModalFormProps {
    onClose: () => void;
    onSubmissionComplete: () => void;
    pageName: string;
    title: string;
    subtitle: string;
    image: string;
}

const ModalForm: React.FC<ModalFormProps> = ({ onClose, pageName, onSubmissionComplete, title, subtitle, image }) => {
    const inputStyle = "p-2 md:p-4 rounded border-gray-200 border-1 font-jarkarta bg-white text-black placeholder-black focus:ring-custom-blue block w-full appearance-none focus:outline-none focus:ring-0 focus:border-custom-blue peer";
    const labelStyle = "absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-custom-blue peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1 pointer-events-none";

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [fullName, setFullName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    const ref = useOnclickOutside(() => { onClose(); });
    usePreventBodyScroll();

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nameParts = e.target.value.split(' ');
        setFullName(e.target.value);
        if (nameParts.length > 1) {
            setFirstName(nameParts.shift() || '');
            setLastName(nameParts.join(' '));
        }
        else {
            setFirstName(nameParts[0] || '');
            setLastName('');
        }
    };

    const checkSubmission = (submitted: boolean) => {
        if (submitted) {
            setIsSubmitted(true);
            onSubmissionComplete();
            setTimeout(() => {
                onClose();
            }, 1250);
        }
    };

    const formFields = (
        <div className='grid md:grid-cols-2 gap-4 w-full'>
            <div className="relative md:col-span-2">
                <input
                    type="text"
                    placeholder=""
                    name="name"
                    value={fullName}
                    onChange={handleNameChange}
                    required
                    className={`${inputStyle} md:col-span-2`}
                />
                <label htmlFor="name" className={labelStyle}>Name<span className="text-red-500">*</span></label>
            </div>
            <input type="hidden" name="first_name" value={firstName} />
            <input type="hidden" name="last_name" value={lastName} />
            <div className="relative">
                <input type="email" placeholder="" name="email" required className={`${inputStyle}`} />
                <label htmlFor="email" className={labelStyle}>Email<span className="text-red-500">*</span></label>
            </div>
            <div className="relative">
                <input type="text" placeholder="" name="zip" required pattern="[0-9]*" className={`${inputStyle}`} />
                <label htmlFor="zip" className={labelStyle}>Zip Code<span className="text-red-500">*</span></label>
            </div>
            <div className="relative">
                <input type="tel" placeholder="" name="phone" className={`${inputStyle}`} />
                <label htmlFor="phone" className={labelStyle}>Phone Number</label>
            </div>
            <div className="relative">
                <input type="text" placeholder="" name="company" className={`${inputStyle}`} />
                <label htmlFor="company" className={labelStyle}>Company</label>
            </div>
        </div>
    );

    const submitButton = (
        <SimpleButton
            size="medium"
            label="Unlock Resources"
            textColor="text-white"
            bgColor="bg-custom-black hover:bg-opacity-80"
            onClick={() => { }}
            roundness=''
            fullWidth={true}
            growScale={1}
        />
    );


    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center font-jarkarta transition-opacity"
        >
            <motion.div
                className='relative h-full xl:h-[700px]'
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                ref={ref}
            >
                <div className="bg-white overflow-scroll md:overflow-hidden shadow-xl transform transition-all h-full w-full md:max-w-screen-lg rounded-lg">
                    <div className='flex h-full flex-col md:flex-row'>
                        <div className='w-full md:w-2/5 bg-cover bg-left relative ' style={{ backgroundImage: `url(${image})` }}>
                            <div className="w-full h-full flex flex-col justify-center items-start p-12 backdrop-brightness-50">
                                <div className="text-white text-lg md:text-4xl font-semibold mb-6">{title}</div>
                                <TripleLine />
                                <div className="text-white text-sm md:text-lg mt-2">{subtitle}</div>
                            </div>
                        </div>
                        <div className='p-12 w-full md:w-3/5'>
                            {isSubmitted ? (
                                <div className="flex justify-center items-center h-full">
                                    <div className="text-center">
                                        <Complete
                                            onComplete={() => { }}
                                            text="Access Unlocked!"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <SalesforceForm
                                        debug={true}
                                        page={pageName}
                                        children={formFields}
                                        button={submitButton}
                                        enableReCAPTCHA={false}
                                        submissionCheck={checkSubmission}
                                        formStyle='flex flex-col justify-start md:justify-center items-center w-full'
                                        loadingCallback={() => { }}
                                    />
                                    <div className='text-xs md:absolute bottom-4 m-2'>
                                        We use your information to personalize your experience and deliver tailored content such as newsletters. For more on how we handle your data, please read our <Link to={"/privacy-policy"} className='text-custom-blue'>Privacy Policy</Link>.
                                    </div>
                                    <button onClick={onClose} className="text-gray-600 hover:text-gray-800 absolute top-4 right-4 z-30">
                                        <IoCloseCircleOutline className="text-3xl" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default ModalForm;
